import React, { useState } from 'react';
import { Heart, Users, Leaf, CreditCard, Smartphone, CheckCircle, Gift, Star } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const DonationHub = () => {
  const [donationAmount, setDonationAmount] = useState('');
  const [donationType, setDonationType] = useState('one-time');
  const [selectedCampaign, setSelectedCampaign] = useState('general');
  const [paymentMethod, setPaymentMethod] = useState('card');

  const presetAmounts = [50, 100, 250, 500, 1000, 2500];
  
  const campaigns = [
    {
      id: 'general',
      name: 'Apoyo General',
      description: 'Contribuye al funcionamiento general de la plataforma',
      goal: 50000,
      raised: 32450,
      supporters: 127
    },
    {
      id: 'student-subsidies',
      name: 'Subsidios Estudiantiles',
      description: 'Ayuda a estudiantes de Universidad Panamericana con recursos limitados',
      goal: 25000,
      raised: 18900,
      supporters: 89
    },
    {
      id: 'business-training',
      name: 'Capacitación Negocios',
      description: 'Programas de entrenamiento para negocios locales de Guadalajara',
      goal: 15000,
      raised: 8750,
      supporters: 34
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Enhanced Navigation Header */}
      <header className="bg-white/90 backdrop-blur-md shadow-lg border-b border-purple-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img 
                src="public/assets/images/slazzer-preview-av4u0-1762512006110.png" 
                alt="Food Loop - Universidad Panamericana"
                className="h-16 w-auto"
              />
              <div className="ml-3">
                <span className="text-2xl font-bold text-gray-900">Food Loop</span>
                <p className="text-sm text-purple-600 font-medium">Centro de Donaciones</p>
              </div>
            </div>

            <nav className="hidden md:flex items-center space-x-8">
              <a href="/homepage" className="text-gray-700 hover:text-purple-600 font-medium">Inicio</a>
              <a href="/impact" className="text-gray-700 hover:text-purple-600 font-medium">Impacto</a>
              <a href="/about" className="text-gray-700 hover:text-purple-600 font-medium">Acerca de</a>
              <a href="/donation-hub" className="text-purple-600 font-bold">Donar</a>
            </nav>

            <div className="flex items-center space-x-4">
              <a href="/signin">
                <Button variant="ghost" className="text-gray-700 hover:text-purple-600">
                  Iniciar Sesión
                </Button>
              </a>
              <a href="/signup">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                  Registrarse
                </Button>
              </a>
            </div>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Dona para{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              Transformar Vidas
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Tu donación ayuda a estudiantes de Universidad Panamericana a acceder a alimentos nutritivos mientras reducimos el desperdicio alimentario en Guadalajara.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Donation Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Hacer una Donación</h2>
              
              {/* Donation Type */}
              <div className="mb-8">
                <label className="block text-lg font-semibold text-gray-900 mb-4">Tipo de Donación</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    className={`p-4 rounded-lg border-2 transition-all ${
                      donationType === 'one-time' ?'border-purple-600 bg-purple-50 text-purple-900' :'border-gray-300 hover:border-purple-300'
                    }`}
                    onClick={() => setDonationType('one-time')}
                  >
                    <Gift className="h-6 w-6 mx-auto mb-2" />
                    <div className="font-semibold">Una Vez</div>
                    <div className="text-sm text-gray-600">Donación única</div>
                  </button>
                  <button
                    className={`p-4 rounded-lg border-2 transition-all ${
                      donationType === 'monthly' ?'border-purple-600 bg-purple-50 text-purple-900' :'border-gray-300 hover:border-purple-300'
                    }`}
                    onClick={() => setDonationType('monthly')}
                  >
                    <Heart className="h-6 w-6 mx-auto mb-2" />
                    <div className="font-semibold">Mensual</div>
                    <div className="text-sm text-gray-600">Apoyo recurrente</div>
                  </button>
                </div>
              </div>

              {/* Campaign Selection */}
              <div className="mb-8">
                <label className="block text-lg font-semibold text-gray-900 mb-4">Selecciona una Campaña</label>
                <div className="space-y-4">
                  {campaigns?.map((campaign) => (
                    <div
                      key={campaign?.id}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedCampaign === campaign?.id
                          ? 'border-purple-600 bg-purple-50' :'border-gray-300 hover:border-purple-300'
                      }`}
                      onClick={() => setSelectedCampaign(campaign?.id)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold text-gray-900">{campaign?.name}</h4>
                        <span className="text-sm text-gray-600">{campaign?.supporters} donantes</span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3">{campaign?.description}</p>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Progreso</span>
                        <span className="font-semibold">
                          ${campaign?.raised?.toLocaleString()} / ${campaign?.goal?.toLocaleString()} MXN
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-gradient-to-r from-purple-600 to-pink-600 h-3 rounded-full"
                          style={{width: `${(campaign?.raised / campaign?.goal) * 100}%`}}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Amount Selection */}
              <div className="mb-8">
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  Cantidad {donationType === 'monthly' ? '(Mensual)' : ''}
                </label>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  {presetAmounts?.map((amount) => (
                    <button
                      key={amount}
                      className={`p-4 rounded-lg border-2 font-semibold transition-all ${
                        donationAmount === amount?.toString()
                          ? 'border-purple-600 bg-purple-600 text-white' :'border-gray-300 hover:border-purple-300'
                      }`}
                      onClick={() => setDonationAmount(amount?.toString())}
                    >
                      ${amount} MXN
                    </button>
                  ))}
                </div>
                <Input
                  type="number"
                  placeholder="Cantidad personalizada"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e?.target?.value)}
                  className="text-lg p-4"
                />
              </div>

              {/* Payment Method */}
              <div className="mb-8">
                <label className="block text-lg font-semibold text-gray-900 mb-4">Método de Pago</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    className={`p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'card' ?'border-purple-600 bg-purple-50 text-purple-900' :'border-gray-300 hover:border-purple-300'
                    }`}
                    onClick={() => setPaymentMethod('card')}
                  >
                    <CreditCard className="h-6 w-6 mx-auto mb-2" />
                    <div className="font-semibold">Tarjeta</div>
                    <div className="text-sm text-gray-600">Crédito o Débito</div>
                  </button>
                  <button
                    className={`p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'digital' ?'border-purple-600 bg-purple-50 text-purple-900' :'border-gray-300 hover:border-purple-300'
                    }`}
                    onClick={() => setPaymentMethod('digital')}
                  >
                    <Smartphone className="h-6 w-6 mx-auto mb-2" />
                    <div className="font-semibold">Digital</div>
                    <div className="text-sm text-gray-600">PayPal, OXXO</div>
                  </button>
                </div>
              </div>

              {/* Donation Impact Preview */}
              {donationAmount && (
                <div className="mb-8 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2" />
                    Tu Impacto Proyectado
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {Math.floor(parseInt(donationAmount) / 25) || 0}
                      </div>
                      <div className="text-sm text-gray-600">Estudiantes Beneficiados</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {Math.floor(parseInt(donationAmount) / 15) || 0}
                      </div>
                      <div className="text-sm text-gray-600">Bolsas Subsidiadas</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">
                        {Math.floor(parseInt(donationAmount) / 10) || 0}kg
                      </div>
                      <div className="text-sm text-gray-600">CO₂ Evitado</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Donate Button */}
              <Button 
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg py-4"
                disabled={!donationAmount}
              >
                <Heart className="mr-2 h-5 w-5" />
                Donar ${donationAmount || '0'} MXN {donationType === 'monthly' ? 'Mensualmente' : 'Ahora'}
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Donor Recognition */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Niveles de Reconocimiento</h3>
              <div className="space-y-4">
                <div className="flex items-center p-4 bg-yellow-50 rounded-lg">
                  <Star className="h-6 w-6 text-yellow-500 mr-3" />
                  <div>
                    <div className="font-semibold text-gray-900">Amigo ($50+)</div>
                    <div className="text-sm text-gray-600">Reporte de impacto trimestral</div>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-purple-50 rounded-lg">
                  <Users className="h-6 w-6 text-purple-500 mr-3" />
                  <div>
                    <div className="font-semibold text-gray-900">Embajador ($250+)</div>
                    <div className="text-sm text-gray-600">Invitación a eventos especiales</div>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-green-50 rounded-lg">
                  <Leaf className="h-6 w-6 text-green-500 mr-3" />
                  <div>
                    <div className="font-semibold text-gray-900">Guardián ($1000+)</div>
                    <div className="text-sm text-gray-600">Reconocimiento público + badge</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Donors */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Donantes Recientes</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <img 
                    src="/foodloop/assets/images/no_image.png" 
                    alt="Ana López - Universidad Panamericana"
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="ml-3">
                    <div className="font-semibold text-gray-900">Ana L.</div>
                    <div className="text-sm text-gray-600">$500 MXN • hace 2 horas</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <img 
                    src="/foodloop/assets/images/no_image.png" 
                    alt="Carlos Mendoza - Alumni Universidad Panamericana"
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="ml-3">
                    <div className="font-semibold text-gray-900">Carlos M.</div>
                    <div className="text-sm text-gray-600">$250 MXN • hace 5 horas</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <img 
                    src="/foodloop/assets/images/no_image.png" 
                    alt="María García - Profesora Universidad Panamericana"
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="ml-3">
                    <div className="font-semibold text-gray-900">María G.</div>
                    <div className="text-sm text-gray-600">$1000 MXN • hace 1 día</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Information */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Información Fiscal</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                  <div className="text-sm text-gray-700">
                    <strong>Deducible de impuestos:</strong> Todas las donaciones son deducibles según la ley mexicana
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                  <div className="text-sm text-gray-700">
                    <strong>Recibo automático:</strong> Recibirás tu comprobante por email
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                  <div className="text-sm text-gray-700">
                    <strong>Transparencia total:</strong> Reportes detallados de uso de fondos
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationHub;